import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  HiCheckCircle,
  HiClock,
  HiExclamationCircle,
  HiGlobe,
  HiOfficeBuilding,
  HiRefresh,
} from 'react-icons/hi'
import Header from '../components/Header'
import OtpAccessPanel from '../components/OtpAccessPanel'
import CrawlReportView from '../components/CrawlReportView'
import {
  REPORT_ERROR,
  ReportAccessError,
  clearReportAccess,
  fetchLeadCrawlReport,
  fetchLeadStatus,
  getStoredReport,
  grantReportAccess,
  hasReportAccess,
  isOtpRetryableError,
  isReportReadyStatus,
} from '../constants/inviteCode'

const getErrorKind = (err) =>
  err instanceof ReportAccessError ? err.kind : REPORT_ERROR.UNKNOWN

const CityReport = () => {
  const { domain: encodedDomain } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const domain = encodedDomain ? decodeURIComponent(encodedDomain) : ''
  const lead = searchParams.get('lead') || ''
  const otpFromUrl = searchParams.get('otp') || ''
  const emailFromUrl = searchParams.get('em') || ''

  const [isUnlocked, setIsUnlocked] = useState(false)
  const [, setOtp] = useState(otpFromUrl)
  const [email, setEmail] = useState(emailFromUrl)
  const [reportPayload, setReportPayload] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [errorKind, setErrorKind] = useState(null)
  const [leadStatus, setLeadStatus] = useState(null)
  const [checkingStatus, setCheckingStatus] = useState(false)

  const handleFetchFailure = useCallback((err, { hadPriorAccess = false } = {}) => {
    const kind = getErrorKind(err)
    const message = err?.message || 'Unable to load report.'

    setReportPayload(null)
    setError(message)
    setErrorKind(kind)

    if (kind === REPORT_ERROR.NOT_FOUND || kind === REPORT_ERROR.NOT_READY) {
      clearReportAccess(lead)
      setIsUnlocked(false)
      return
    }

    if (kind === REPORT_ERROR.INVALID_OTP) {
      if (hadPriorAccess) {
        clearReportAccess(lead)
      }
      setIsUnlocked(false)
    }
  }, [lead])

  const unlockWithCredentials = useCallback(async (accessCode, leadEmail) => {
    const data = await fetchLeadCrawlReport(domain, accessCode, leadEmail)
    grantReportAccess(lead, { domain, lead, otp: accessCode, email: leadEmail })
    setOtp(accessCode)
    setEmail(leadEmail)
    setReportPayload(data)
    setError('')
    setErrorKind(null)
    setIsUnlocked(true)
    setLoading(false)
    return data
  }, [domain, lead])

  const checkLeadStatus = useCallback(async () => {
    if (!lead) return

    setCheckingStatus(true)
    try {
      const statusData = await fetchLeadStatus(lead)
      setLeadStatus(statusData.status)

      if (isReportReadyStatus(statusData.status)) {
        setError((prev) =>
          prev.includes('still being prepared')
            ? 'Your report is ready. Check your email for the access code, or enter it below.'
            : prev,
        )
        setErrorKind(REPORT_ERROR.INVALID_OTP)
      }
    } catch (err) {
      setLeadStatus('unknown')
      console.error('Lead status check failed:', err)
    } finally {
      setCheckingStatus(false)
    }
  }, [lead])

  useEffect(() => {
    if (!domain || !lead) return

    const stored = getStoredReport(lead)
    const accessCode = otpFromUrl || stored?.otp
    const leadEmail = emailFromUrl || stored?.email

    if (accessCode) setOtp(accessCode)
    if (leadEmail) setEmail(leadEmail)

    if (accessCode && leadEmail) {
      unlockWithCredentials(accessCode, leadEmail).catch((err) => {
        handleFetchFailure(err, { hadPriorAccess: hasReportAccess(lead) })
        setLoading(false)
      })
      return
    }

    setLoading(false)
  }, [domain, lead, otpFromUrl, emailFromUrl, unlockWithCredentials, handleFetchFailure])

  useEffect(() => {
    if (
      errorKind === REPORT_ERROR.NOT_FOUND ||
      errorKind === REPORT_ERROR.NOT_READY
    ) {
      checkLeadStatus()
    }
  }, [errorKind, checkLeadStatus])

  const handleOtpVerified = ({ otp: code, email: leadEmail }) => {
    setLoading(true)
    setError('')
    setErrorKind(null)
    unlockWithCredentials(code, leadEmail).catch((err) => {
      handleFetchFailure(err, { hadPriorAccess: hasReportAccess(lead) })
      setLoading(false)
    })
  }

  if (!domain || !lead) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Header />
        <main className="max-w-3xl mx-auto px-4 pt-28 pb-16 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            Invalid report link. A valid link includes the city domain and lead ID.
          </p>
        </main>
      </div>
    )
  }

  const leadEmail = email || emailFromUrl || getStoredReport(lead)?.email || ''
  const showOtpPanel =
    leadEmail &&
    (!error || isOtpRetryableError(errorKind)) &&
    errorKind !== REPORT_ERROR.NOT_FOUND &&
    errorKind !== REPORT_ERROR.NOT_READY

  const errorTitle =
    errorKind === REPORT_ERROR.NOT_FOUND
      ? 'Report not available'
      : errorKind === REPORT_ERROR.NOT_READY
        ? 'Report still preparing'
        : 'Unable to load report'

  const statusLabel = (() => {
    if (!leadStatus) return null
    if (isReportReadyStatus(leadStatus)) return 'Your report is ready — check your email for the access code.'
    if (leadStatus === 'failed') return 'Report preparation failed. Return to city signup to try again.'
    if (leadStatus === 'processing' || leadStatus === 'in_progress') {
      return 'We are still preparing your city discovery report.'
    }
    return `Current status: ${leadStatus}`
  })()

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        {!isUnlocked && !loading && !error ? (
          <section className="max-w-lg mx-auto text-center">
            <HiOfficeBuilding className="h-12 w-12 text-primary-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              City Discovery Report
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-8">{domain}</p>
            {showOtpPanel ? (
              <OtpAccessPanel
                email={leadEmail}
                domain={domain}
                onVerified={handleOtpVerified}
              />
            ) : (
              <p className="text-gray-600 dark:text-gray-400">
                Open the report link from your email to view this report.
              </p>
            )}
          </section>
        ) : loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading report…</p>
          </div>
        ) : error ? (
          <section className="max-w-lg mx-auto text-center">
            {errorKind === REPORT_ERROR.NOT_READY ? (
              <HiClock className="h-12 w-12 text-blue-500 mx-auto mb-4" />
            ) : (
              <HiExclamationCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            )}
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {errorTitle}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>

            {statusLabel && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{statusLabel}</p>
            )}

            {(errorKind === REPORT_ERROR.NOT_FOUND || errorKind === REPORT_ERROR.NOT_READY) && (
              <div className="flex flex-col items-center gap-3 mb-8">
                <button
                  type="button"
                  onClick={checkLeadStatus}
                  disabled={checkingStatus}
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 disabled:opacity-50"
                >
                  <HiRefresh className={`h-4 w-4 ${checkingStatus ? 'animate-spin' : ''}`} />
                  {checkingStatus ? 'Checking status…' : 'Check report status'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/cities')}
                  className="text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                >
                  Return to city signup
                </button>
              </div>
            )}

            {showOtpPanel && (
              <OtpAccessPanel
                email={leadEmail}
                domain={domain}
                onVerified={handleOtpVerified}
              />
            )}
          </section>
        ) : (
          <>
            <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-1">
                  City Discovery Report
                </p>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {domain}
                </h1>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <HiGlobe className="h-4 w-4 shrink-0" />
                  <span>{domain}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <HiCheckCircle className="h-5 w-5 text-green-500" />
                <span className="text-sm font-medium text-green-800 dark:text-green-200">
                  Verified
                </span>
              </div>
            </div>

            <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-hidden">
              <CrawlReportView report={reportPayload} domain={domain} />
            </section>

            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={() => navigate('/cities')}
                className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
              >
                ← Back to city signup
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  )
}

export default CityReport
